import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content, allowing common formatting tags.
 * Use for user-generated rich text, CMS content, etc.
 */
export function sanitizeHtml(dirty: string): string {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [
      'b',
      'i',
      'em',
      'strong',
      'a',
      'p',
      'br',
      'ul',
      'ol',
      'li',
      'span',
      'div',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'img',
      'table',
      'thead',
      'tbody',
      'tr',
      'th',
      'td',
      'blockquote',
      'pre',
      'code',
      'hr',
      'sup',
      'sub',
      'u',
      's',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style', 'src', 'alt', 'width', 'height'],
    ALLOW_DATA_ATTR: false,
  });
}

/**
 * Sanitize embed/oembed HTML (e.g. from Iframely, YouTube embeds).
 * More permissive — allows iframes, blockquotes, and embed-related tags
 * but still strips scripts and event handlers.
 */
export function sanitizeEmbedHtml(dirty: string): string {
  return DOMPurify.sanitize(dirty, {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: [
      'allow',
      'allowfullscreen',
      'frameborder',
      'scrolling',
      'loading',
      'referrerpolicy',
      'sandbox',
    ],
    ALLOW_DATA_ATTR: true,
  });
}

/**
 * Strip all HTML, returning plain text only.
 */
export function sanitizeText(dirty: string): string {
  return DOMPurify.sanitize(dirty, { ALLOWED_TAGS: [] });
}

/**
 * Sanitize custom CSS for injection into a <style> tag.
 * Strips HTML tags while preserving CSS text content.
 */
export function sanitizeCustomCss(dirty: string): string {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
  });
}

/**
 * Validate a URL to ensure it uses a safe protocol (http/https).
 * Returns the URL string if safe, or null otherwise.
 */
export function sanitizeUrl(url: string): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (['http:', 'https:'].includes(parsed.protocol)) {
      return url;
    }
  } catch {
    // invalid URL
  }
  return null;
}
